/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Import routes
import routes from "routes";

function BaseLayout({ stickyNavbar = false, children }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Get available tugas routes
  const availableTugasRoutes = useMemo(() => {
    const tugasSection = routes
      .find((route) => route.key === "pages")
      ?.collapse?.find((section) => section.key === "tugas");
    if (!tugasSection?.collapse) return [];

    return tugasSection.collapse.filter((route) => route.available === true);
  }, []);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  // Set tab value based on current route
  useEffect(() => {
    const path = location.pathname;
    const currentRouteIndex = availableTugasRoutes.findIndex((route) =>
      path.includes(route.route)
    );

    if (currentRouteIndex !== -1) {
      setTabValue(currentRouteIndex);
    } else if (availableTugasRoutes.length > 0) {
      // If current route is not found, default to first available route
      setTabValue(0);
    }
  }, [location.pathname, availableTugasRoutes]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);

    // Navigate to corresponding route based on tab value
    if (availableTugasRoutes[newValue]) {
      navigate(availableTugasRoutes[newValue].route);
    }
  };

  // Don't render tabs if no available routes
  if (availableTugasRoutes.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar absolute={!stickyNavbar} isMini />
        <MDBox mt={stickyNavbar ? 3 : 10}>{children}</MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar absolute={!stickyNavbar} isMini />
      <MDBox mt={stickyNavbar ? 3 : 10}>
        <Grid container>
          <Grid item xs={12} sm={8} lg={4}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue}
                onChange={handleSetTabValue}
              >
                {availableTugasRoutes.map((route, index) => (
                  <Tab key={route.key} label={route.name} />
                ))}
              </Tabs>
            </AppBar>
          </Grid>
        </Grid>
        {children}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

// Typechecking props for BaseLayout
BaseLayout.propTypes = {
  stickyNavbar: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default BaseLayout;
